import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { toast } from "sonner";
import TournamentBracket from "@/components/TournamentBracket";
import { ArrowLeft, RotateCw, Shuffle, Play, Users, Clock, CheckCircle2, Zap } from "lucide-react";

const STATUS_CONFIG = {
  waiting_for_players: { label: "Waiting for Players", color: "text-white/30",   bg: "bg-white/[0.02] border-white/8",            dot: "bg-white/20" },
  pending:             { label: "Ready — Start Match",  color: "text-yellow-400", bg: "bg-yellow-400/10 border-yellow-400/30",      dot: "bg-yellow-400" },
  waiting_admin:       { label: "Ready — Start Match",  color: "text-yellow-400", bg: "bg-yellow-400/10 border-yellow-400/30",      dot: "bg-yellow-400" },
  active:              { label: "In Progress",          color: "text-emerald-400", bg: "bg-emerald-400/10 border-emerald-400/30",   dot: "bg-emerald-400 animate-pulse" },
  done:                { label: "Completed",            color: "text-white/40",   bg: "bg-white/5 border-white/10",                 dot: "bg-white/40" },
};

function MatchCard({ slot, levelLabel, onStart, starting, levelLocked }) {
  const hasPlayers = !!(slot.p1_pid || slot.p2_pid);
  const hasBothPlayers = !!(slot.p1_pid && slot.p2_pid);
  const canStart = !levelLocked && (slot.status === "pending" || slot.status === "waiting_admin") && hasBothPlayers;

  // Derive a display status accounting for level lock and missing players
  let displayStatus = slot.status;
  if (levelLocked) displayStatus = "locked";
  else if (!hasPlayers) displayStatus = "waiting_for_players";
  else if (hasPlayers && !hasBothPlayers) displayStatus = "waiting_for_players";

  const STATUS_CONFIG_EX = {
    ...STATUS_CONFIG,
    locked: { label: "Locked — prev round pending", color: "text-white/20", bg: "bg-white/[0.01] border-white/5", dot: "bg-white/10" },
  };
  const cfg = STATUS_CONFIG_EX[displayStatus] || STATUS_CONFIG_EX.pending;

  return (
    <div className={`rounded-md border p-4 flex flex-col gap-3 ${cfg.bg}`} data-testid={`match-card-${slot.m}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40">{levelLabel} ·</span>
          <span className="font-display text-lg text-white">Match #{slot.m}</span>
        </div>
        <div className={`flex items-center gap-1.5 font-mono-kl text-[9px] uppercase tracking-widest ${cfg.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
          {cfg.label}
        </div>
      </div>

      {/* Players */}
      <div className="grid grid-cols-2 gap-2">
        {[slot.p1, slot.p2].map((p, i) => {
          const isWinner = slot.status === "done" && slot.winner_pid === p?.pid;
          const filled = i === 0 ? !!slot.p1_pid : !!slot.p2_pid;
          return (
            <div key={i} className={`rounded border p-2.5 flex items-start gap-2 ${
              isWinner ? "border-[color:var(--kl-red)]/50 bg-[rgba(230,57,70,0.08)]"
              : filled ? "border-white/10" : "border-dashed border-white/8"
            }`} style={!isWinner ? { background: filled ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.01)" } : {}}>
              {/* Mini avatar */}
              <div className="shrink-0 w-9 h-11 rounded overflow-hidden border border-white/10"
                style={{ background: "rgba(255,255,255,0.05)" }}>
                {p?.avatar?.img ? (
                  <img src={p.avatar.img} alt={p.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/20 text-xs font-bold">
                    {filled ? (p?.name || "?")[0].toUpperCase() : "—"}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-mono-kl text-[8px] uppercase tracking-widest text-white/30 mb-0.5">Player {i + 1}</div>
                <div className={`font-display text-sm uppercase truncate ${filled ? "text-white" : "text-white/20"}`}>
                  {filled ? (p?.name || "—") : "TBD"}
                </div>
                <div className="font-mono-kl text-[9px] text-white/40 truncate">
                  {filled ? (p?.phone || p?.email || "—") : (i === 0 ? "Waiting for player" : "Waiting for player")}
                </div>
                {slot.status === "done" && (
                  <div className="font-mono-kl text-[9px] mt-0.5 flex items-center gap-1">
                    {isWinner && <span className="text-[color:var(--kl-red)]">WINNER</span>}
                    {slot[`p${i + 1}_total`] != null && (
                      <span className="text-white/50">{isWinner ? "·" : ""} {slot[`p${i + 1}_total`]} pts</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action */}
      {canStart && (
        <button
          onClick={onStart}
          disabled={!!starting}
          className="btn-submit w-full justify-center"
          data-testid={`start-match-${slot.m}`}
        >
          {starting ? (
            <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" /> Starting…</>
          ) : (
            <><Play className="w-4 h-4" /> Start Match</>
          )}
        </button>
      )}

      {slot.status === "active" && (
        <div className="flex items-center gap-2 text-emerald-400 font-mono-kl text-[9px] uppercase tracking-widest">
          <Zap className="w-3 h-3" /> Battle in progress — players are fighting!
        </div>
      )}
    </div>
  );
}

const LEVEL_LABELS = { L0: "Quarter Final", L1: "Semi Final", L2: "Final", L3: "Bronze" };

function MatchManagementPanel({ tournament, onStartMatch, startingMatch }) {
  const bracket = tournament.bracket || {};

  // Per-level slot arrays
  const l0 = (bracket.L0 || []).map((s) => ({ ...s, level: "L0" }));
  const l1 = (bracket.L1 || []).map((s) => ({ ...s, level: "L1" }));
  const l2 = (bracket.L2 || []).map((s) => ({ ...s, level: "L2" }));
  const l3 = (bracket.L3 || []).map((s) => ({ ...s, level: "L3" }));

  // Level-gate logic
  const l0AllDone  = l0.length > 0 && l0.every((s) => s.status === "done");
  const l1AllDone  = l1.length > 0 && l1.every((s) => s.status === "done");

  const maxPlayers  = tournament.max_players || 8;
  const currentCount = tournament.current_player_count ?? (tournament.players?.length ?? 0);
  const capacityPct  = Math.min(100, (currentCount / maxPlayers) * 100);

  const l0ReadyCount = l0.filter((s) => s.status === "pending" || s.status === "waiting_admin").length;
  const l0Active     = l0.filter((s) => s.status === "active").length;
  const l0Done       = l0.filter((s) => s.status === "done").length;

  const LEVEL_SECTIONS = [
    { key: "L0", label: "Quarter Finals — L0", sub: "4 matches · 8 players", slots: l0, locked: false,    lockMsg: "" },
    { key: "L1", label: "Semi Finals — L1",    sub: "2 matches · L0 winners", slots: l1, locked: !l0AllDone, lockMsg: `Complete all ${l0.length} L0 matches first (${l0Done}/${l0.length} done)` },
    { key: "L2", label: "Final — L2",          sub: "1 match · L1 winners",  slots: l2, locked: !l1AllDone, lockMsg: "Complete all L1 Semi Finals first" },
    { key: "L3", label: "3rd Place — Bronze",  sub: "1 match · L1 losers",   slots: l3, locked: !l1AllDone, lockMsg: "Complete all L1 Semi Finals first" },
  ];

  return (
    <div className="mt-8">
      {/* Capacity bar */}
      <div className="mb-6 rounded-md border border-white/10 p-4 flex flex-col gap-2" style={{ background: "rgba(255,255,255,0.02)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-white/60" />
            <span className="font-mono-kl text-xs uppercase tracking-widest text-white/60">Tournament Capacity</span>
            {tournament.auto_created && (
              <span className="font-mono-kl text-[8px] uppercase tracking-widest text-yellow-400/60 border border-yellow-400/20 rounded px-1.5 py-0.5">Auto-Created</span>
            )}
          </div>
          <span className="font-mono-kl text-sm text-white">
            {currentCount}<span className="text-white/40">/{maxPlayers} players</span>
          </span>
        </div>
        <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
          <div className="h-full rounded-full transition-all duration-500"
            style={{ width: `${capacityPct}%`, background: currentCount >= maxPlayers ? "var(--kl-red)" : "#facc15" }} />
        </div>
        <div className="flex items-center gap-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/30">
          <span className="text-yellow-400">{l0ReadyCount} L0 ready to start</span>
          <span>·</span>
          <span className="text-emerald-400">{l0Active} active</span>
          <span>·</span>
          <span className="text-white/20">{l0Done}/{l0.length} L0 done</span>
          <span>·</span>
          <span className={l0AllDone ? "text-yellow-400" : "text-white/20"}>L1 {l0AllDone ? "unlocked" : "locked"}</span>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <Users className="w-4 h-4 text-white/60" />
        <span className="font-mono-kl text-xs uppercase tracking-widest text-white/60">Match Management · By Round</span>
      </div>

      {/* Level sections */}
      {LEVEL_SECTIONS.map(({ key, label, sub, slots, locked, lockMsg }) => (
        <div key={key} className={`mb-8 ${locked ? "opacity-50" : ""}`}>
          {/* Level header */}
          <div className={`rounded-md border px-4 py-3 mb-4 flex items-center justify-between ${
            locked ? "border-white/8 bg-white/[0.01]" : key === "L0" ? "border-[color:var(--kl-red)]/30 bg-[rgba(230,57,70,0.04)]" : "border-yellow-400/20 bg-yellow-400/[0.03]"
          }`}>
            <div>
              <div className="font-display text-base uppercase text-white">{label}</div>
              <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/35 mt-0.5">{sub}</div>
            </div>
            {locked ? (
              <div className="flex items-center gap-2 font-mono-kl text-[9px] uppercase tracking-widest text-white/30">
                <span className="text-lg">🔒</span> {lockMsg}
              </div>
            ) : (
              <div className="flex items-center gap-2 font-mono-kl text-[9px] uppercase tracking-widest">
                <span className={key === "L0" ? "text-[color:var(--kl-red)]" : "text-yellow-400"}>Unlocked</span>
                {key !== "L0" && <span className="text-white/30">— Winners advance from {key === "L1" ? "L0" : "L1"}</span>}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {slots.map((slot) => (
              <MatchCard
                key={`${key}_${slot.m}`}
                slot={slot}
                levelLabel={LEVEL_LABELS[key] || key}
                onStart={locked ? null : () => onStartMatch(key, slot.m)}
                starting={startingMatch === `${key}_${slot.m}`}
                levelLocked={locked}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Completed section (cross-level) */}
      {[...l0, ...l1, ...l2, ...l3].filter((s) => s.status === "done").length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-3.5 h-3.5 text-white/40" />
            <span className="font-mono-kl text-[10px] uppercase tracking-widest text-white/40">Completed Matches</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...l0, ...l1, ...l2, ...l3].filter((s) => s.status === "done").map((slot) => (
              <MatchCard
                key={`${slot.level}_${slot.m}`}
                slot={slot}
                levelLabel={LEVEL_LABELS[slot.level] || slot.level}
                onStart={null}
                starting={null}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminTournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.tournamentGet(id);
      setT(r);
    } catch (e) {
      toast.error("Failed to load tournament");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const seedFromRegistered = async (count) => {
    setSeeding(true);
    try {
      await api.tournamentSeedFromRegistered(id, count);
      toast.success(`${count} players randomly drawn and assigned!`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to seed players");
    } finally { setSeeding(false); }
  };

  const [startingMatch, setStartingMatch] = useState(null); // level_matchnum key being started

  const startMatch = async (level, match_num) => {
    const key = `${level}_${match_num}`;
    setStartingMatch(key);
    try {
      const r = await api.tournamentStartMatch(id, level, match_num);
      toast.success(`Match #${match_num} started! Players notified.`);
      if (r.bye) {
        toast.info(`Bye match — ${r.winner} advances automatically.`);
        await load();
        return;
      }
      // Navigate admin/kiosk to the battle screen
      const t2 = await api.tournamentGet(id);
      const slots = t2.bracket[level] || [];
      const slot = slots.find((s) => s.m === match_num);
      if (slot && slot.p1 && slot.p2) {
        sessionStorage.setItem("proctorly_battle", JSON.stringify({
          player1: { name: slot.p1.name, email: slot.p1.email || "", phone: slot.p1.phone || "" },
          player2: { name: slot.p2.name, email: slot.p2.email || "", phone: slot.p2.phone || "" },
          room_id: r.room_id, role: "host",
          tournament: { id, name: t2.name, level, match_num },
        }));
        navigate("/battle");
      } else {
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start match");
    } finally { setStartingMatch(null); }
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "var(--kl-navy-deep)" }}>
      <Header />
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-10 relative">
        <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-15 blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(230,57,70,0.55), transparent 60%)" }} />

        <div className="relative z-10">
          <Link to="/admin" className="font-mono-kl text-xs uppercase tracking-widest text-white/60 hover:text-white inline-flex items-center gap-2 mb-6" data-testid="back-admin">
            <ArrowLeft className="w-4 h-4" /> All tournaments
          </Link>

          {loading && !t && <div className="text-white/60">Loading…</div>}

          {t && (
            <>
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)]">
                  <span className="kl-accent-line mr-3" />TOURNAMENT · {t.status.toUpperCase()}
                </div>
                <button onClick={load} className="btn-ghost-dark text-xs" data-testid="refresh">
                  <RotateCw className="w-3.5 h-3.5 inline mr-1" /> Refresh
                </button>
              </div>
              <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight mb-4">{t.name}</h1>

              {t.status === "pending" && (
                <div className="mb-8 rounded-md border border-[color:var(--kl-red)]/40 p-5"
                  style={{ background: "rgba(230,57,70,0.06)" }}>
                  <div className="font-mono-kl text-xs uppercase tracking-widest text-[color:var(--kl-red)] mb-2">
                    No players assigned yet
                  </div>
                  <p className="text-white/60 text-sm mb-4">
                    Use the random draw to pick from verified registered users, or go back to the tournament list.
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <button onClick={() => seedFromRegistered(8)} disabled={seeding} className="btn-submit">
                      <Shuffle className="w-4 h-4" /> {seeding ? "Drawing…" : "Random Draw — 8 Players"}
                    </button>
                    <button onClick={() => seedFromRegistered(4)} disabled={seeding} className="btn-ghost-dark">
                      <Shuffle className="w-4 h-4" /> {seeding ? "…" : "4 Players"}
                    </button>
                    <button onClick={() => seedFromRegistered(2)} disabled={seeding} className="btn-ghost-dark">
                      <Shuffle className="w-4 h-4" /> {seeding ? "…" : "2 Players (head-to-head)"}
                    </button>
                    <button onClick={() => seedFromRegistered(0)} disabled={seeding} className="btn-ghost-dark text-yellow-400/80 border-yellow-400/20 hover:border-yellow-400/40">
                      <Shuffle className="w-4 h-4" /> {seeding ? "…" : "Best-Fit (all available)"}
                    </button>
                  </div>
                </div>
              )}

              {t.status !== "pending" && (
                <>
                  {/* ── Match Management Panel ───────────────────────────────── */}
                  <MatchManagementPanel tournament={t} onStartMatch={startMatch} startingMatch={startingMatch} />

                  {/* ── Bracket visual ─────────────────────────────────────── */}
                  <div className="mt-10">
                    <TournamentBracket tournament={t} onStartMatch={startMatch} />
                  </div>
                </>
              )}

              <div className="mt-12 rounded-md border border-white/10 p-6" style={{ background: "rgba(255,255,255,0.02)" }}>
                <div className="font-mono-kl text-xs uppercase tracking-widest text-white/50 mb-3">Roster</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {t.players.map((p, i) => (
                    <div key={p.pid} className="rounded-md border border-white/10 p-3" data-testid={`roster-${i + 1}`}>
                      <div className="font-display text-2xl text-[color:var(--kl-red)]">0{i + 1}</div>
                      <div className="font-display text-base uppercase text-white truncate">{p.name}</div>
                      <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40 truncate">{p.phone || p.email || "—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
